/**
 * Centralized SEO helpers: per-route <meta>/<link> management and JSON-LD
 * structured data injection. Consumed by `src/router/index.ts` (route-level
 * title/description/OG/canonical/robots) and by individual views that want
 * to inject their own structured data (e.g. FAQPage on the landing page).
 *
 * Canonical/OG URLs are built from `window.location.origin` rather than a
 * hardcoded domain so staging/preview deploys always self-reference
 * correctly. Those environments are kept out of the index via
 * `public/robots.txt` (which only allows the production host) and via
 * per-route `noindex` meta, not by hardcoding a prod-only origin here.
 */

export const SITE_NAME = 'Lighthouse'

/** Fallback social-share image used when a route doesn't set its own `ogImage`. */
export const DEFAULT_OG_IMAGE = new URL('../assets/images/LighthouseLogoLargeSquare.png', import.meta.url).href

/** The price line in marketing descriptions. Compiled out of the native build: the store apps never show prices. */
export const PRICE_TAGLINE = __LH_NATIVE__ ? '' : ' $99/mo, 0% commission.'

export const DEFAULT_DESCRIPTION =
    "Lighthouse is the AI assistant that runs your brand deals — replying to emails in your voice, reviewing contracts, tracking every project, and chasing invoices." + PRICE_TAGLINE

function absoluteUrl(pathOrUrl: string): string {
    if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl
    return `${window.location.origin}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`
}

type MetaDefinition = { key: string; property?: string; name?: string; content: string }

const MANAGED_META_KEYS = [
    'description',
    'robots',
    'og:type',
    'og:site_name',
    'og:url',
    'og:title',
    'og:description',
    'og:image',
    'twitter:card',
    'twitter:title',
    'twitter:description',
    'twitter:image',
]

export type RouteSeoInput = {
    title?: string
    description?: string
    ogImage?: string
    ogType?: string
    canonicalPath?: string
    /** Marks the page as private/app/auth-only so it's excluded from indexing. */
    noindex?: boolean
}

/**
 * Applies title, description, canonical, OG/Twitter, and robots meta for the
 * current route. Called from `router.afterEach` on every navigation so every
 * route — not just an allowlist — gets correct tags.
 */
export function applyRouteSeo(currentPath: string, input: RouteSeoInput): void {
    const title = input.title ? `${input.title}${input.title.includes(SITE_NAME) ? '' : ` | ${SITE_NAME}`}` : SITE_NAME
    document.title = title

    const description = input.description ?? DEFAULT_DESCRIPTION
    const canonicalUrl = absoluteUrl(input.canonicalPath ?? currentPath)
    const ogImage = absoluteUrl(input.ogImage ?? DEFAULT_OG_IMAGE)

    const metaDefinitions: MetaDefinition[] = [
        { key: 'description', name: 'description', content: description },
        { key: 'robots', name: 'robots', content: input.noindex ? 'noindex, nofollow' : 'index, follow' },
        { key: 'og:type', property: 'og:type', content: input.ogType ?? 'website' },
        { key: 'og:site_name', property: 'og:site_name', content: SITE_NAME },
        { key: 'og:url', property: 'og:url', content: canonicalUrl },
        { key: 'og:title', property: 'og:title', content: title },
        { key: 'og:description', property: 'og:description', content: description },
        { key: 'og:image', property: 'og:image', content: ogImage },
        { key: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
        { key: 'twitter:title', name: 'twitter:title', content: title },
        { key: 'twitter:description', name: 'twitter:description', content: description },
        { key: 'twitter:image', name: 'twitter:image', content: ogImage },
    ]

    // Clear previously-managed tags, then re-add the current set. Simplest
    // way to guarantee no stale tag survives a route with fewer fields set.
    MANAGED_META_KEYS.forEach((key) => {
        document.querySelectorAll(`meta[data-seo-key="${key}"]`).forEach((el) => el.remove())
    })

    metaDefinitions.forEach(({ key, property, name, content }) => {
        const tag = document.createElement('meta')
        if (property) tag.setAttribute('property', property)
        if (name) tag.setAttribute('name', name)
        tag.setAttribute('data-seo-key', key)
        tag.setAttribute('content', content)
        document.head.appendChild(tag)
    })

    let canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!canonicalLink) {
        canonicalLink = document.createElement('link')
        canonicalLink.setAttribute('rel', 'canonical')
        document.head.appendChild(canonicalLink)
    }
    canonicalLink.setAttribute('href', canonicalUrl)
}

/**
 * Injects (or replaces) a JSON-LD `<script type="application/ld+json">` tag
 * identified by `id`. Call `removeJsonLd(id)` on unmount for view-specific
 * schemas (FAQPage, etc.) so they don't leak onto other routes via SPA nav.
 */
export function setJsonLd(id: string, data: Record<string, unknown>): void {
    let script = document.getElementById(id) as HTMLScriptElement | null
    if (!script) {
        script = document.createElement('script')
        script.type = 'application/ld+json'
        script.id = id
        document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(data)
}

export function removeJsonLd(id: string): void {
    document.getElementById(id)?.remove()
}

export function organizationJsonLd() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: window.location.origin,
        logo: new URL('../assets/images/LogoOrange.png', import.meta.url).href,
        sameAs: ['https://www.instagram.com/fromlighthouse', 'https://www.youtube.com/@joinlighthouse'],
    }
}

export function websiteJsonLd() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: window.location.origin,
    }
}

export function softwareApplicationJsonLd(input: { monthlyPrice: number; annualPrice: number; trialDays: number }) {
    return {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: SITE_NAME,
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web, iOS, Android',
        description: DEFAULT_DESCRIPTION,
        offers: [
            {
                '@type': 'Offer',
                price: input.monthlyPrice.toString(),
                priceCurrency: 'USD',
                priceValidUntil: undefined,
                category: 'subscription',
                description: `${input.trialDays}-day free trial, then $${input.monthlyPrice}/month`,
            },
            {
                '@type': 'Offer',
                price: input.annualPrice.toString(),
                priceCurrency: 'USD',
                category: 'subscription',
                description: `${input.trialDays}-day free trial, then $${input.annualPrice}/year`,
            },
        ],
    }
}

export function faqJsonLd(faqs: { q: string; a: string }[]) {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.a,
            },
        })),
    }
}
